import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import {
  BenefitDTOBenefitEnum,
  BulkActivationResultItemDTO,
  CreateBenefitActivationDTO,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import { apis } from 'probonio-shared-ui/module/api';
import { useWithMessage } from 'probonio-shared-ui/module/error';
import { useTenantID } from 'probonio-shared-ui/module/me';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveActivation } from '../ActiveActivation';
import { useRefetchActivations } from '../query';
import { ActivationDialogSaveFunctions } from './ActivationDialog';

export function useEmployeeActivationSaveFunctions(
  benefit: BenefitDTOBenefitEnum,
  employeeId: string,
  customBenefitId?: string,
  onAdjusted?: (result: BulkActivationResultItemDTO) => void,
): ActivationDialogSaveFunctions {
  const { t } = useTranslation('benefitsModule');
  const refetchActivations = useRefetchActivations(employeeId);
  const getTenantId = useTenantID();
  const { enqueueSnackbar } = useSnackbar();

  const withMessage = useWithMessage();

  const handleActivationError = useCallback(
    (err: Error) => {
      const statusCode = (err as AxiosError).response?.status;
      const errorData = (err as AxiosError).response?.data as { data?: { errorKey?: string } } | undefined;
      if (statusCode === 409) {
        enqueueSnackbar(t(`activationError.${errorData?.data?.errorKey || 'conflict'}`), { variant: 'error' });
      } else if (statusCode === 422) {
        enqueueSnackbar(
          t(errorData?.data?.errorKey ? `activationError.${errorData?.data?.errorKey}` : [`inuseError.${benefit}`, 'inuseError.default']),
          { variant: 'error' },
        );
      } else {
        withMessage(err);
      }
    },
    [benefit, enqueueSnackbar, t, withMessage],
  );

  const { activeActivation } = useActiveActivation(employeeId, benefit, false, customBenefitId);

  const createActivationMutation = useMutation({
    mutationFn: async (props: { create: CreateBenefitActivationDTO }) => {
      await apis.benefits.createBenefitActivation({
        tenantId: getTenantId(),
        employeeId,
        createBenefitActivationDTO: props.create,
      });
    },

    onSuccess: () => {
      void refetchActivations();
      enqueueSnackbar(t('successCreate'), { variant: 'success' });
    },
    onError: (err: Error) => handleActivationError(err),
  });

  const updateActivationMutation = useMutation({
    mutationFn: async (props: { benefitActivationId: string; update: UpdateBenefitActivationDTO }) => {
      return await apis.benefits
        .updateBenefitActivation({
          benefitActivationId: props.benefitActivationId,
          tenantId: getTenantId(),
          employeeId,
          updateBenefitActivationDTO: props.update,
        })
        .then(res => res.data);
    },

    onSuccess: result => {
      void refetchActivations();
      if (result.adjustedEndDate) {
        onAdjusted?.({ status: 'ADJUSTMENT', endDate: result.adjustedEndDate });
      } else {
        enqueueSnackbar(t('successUpdate'), { variant: 'success' });
      }
    },
    onError: (err: Error) => handleActivationError(err),
  });

  const activeEndDateISO = activeActivation?.endDate;

  return useMemo(
    () => ({
      activeEndDate: activeEndDateISO ? DateTime.fromISO(activeEndDateISO) : undefined,
      onCreateActivation: create => createActivationMutation.mutateAsync({ create }),
      onUpdateActivation: async (benefitActivationId, update) => {
        await updateActivationMutation.mutateAsync({ benefitActivationId, update });
      },
      onAdjusted,
    }),
    [activeEndDateISO, createActivationMutation, updateActivationMutation, onAdjusted],
  );
}
